// =============================================
// Author:      JUNAID HASSAN
// Create date: 2025-08-18
// Description: Personal tab bound to overlay JSON from API.
//              - Lazy loads once when tab becomes active
//              - Loads: { data, flags, pendingCount, lastSavedAt }
//              - Emits identification_id up to parent (onLoadNationalId)
//              - Exposes getOverlaySnapshot() and refreshOverlay() via ref
// =============================================

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import {
  CSpinner, CFormInput, CFormSelect, CBadge, CAlert,
} from '@coreui/react';
import { useTranslation } from '../../utils/i18nStub';
import { fetchPersonalOverlay, savePersonalDraft } from '../../services/employeeService';

const emptyData = {
  employee_id: null,
  user_id: null,
  identification_id: '',
  name_ar: '',
  name_en: '',
  gender: '',             // 'male' | 'female'
  dob_hijri: '',
  dob_gregorian: '',
  date_of_birth: '',
  created_by: null,
  created_dt: null,
  updated_dt: null,
  updated_by: null,
  odoo_update_dt: null,
};

const EmployeePersonalInfo = forwardRef(({ active = false, onLoadNationalId }, ref) => {
  const { t } = useTranslation(['profile', 'common']);

  // overlay state
  const [form, setForm] = useState(emptyData);
  const [flags, setFlags] = useState({});
  const [meta, setMeta] = useState({ pendingCount: 0, lastSavedAt: null });

  // view state
  const [loading, setLoading] = useState(false);      // start false; we lazy-load
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [snapshot, setSnapshot] = useState(emptyData);
  const [loadedOnce, setLoadedOnce] = useState(false); // guard to fetch only once

  // --- loader (can be forced by parent refreshOverlay) ---
  const loadOverlay = async (force = false) => {
    if (!active && !force) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchPersonalOverlay();
      const data = payload?.data || emptyData;

      const next = {
        ...emptyData,
        ...data,
        date_of_birth: data?.date_of_birth || data?.dob_gregorian || '',
      };

      setForm(next);
      setFlags(payload?.flags || {});
      setMeta({
        pendingCount: payload?.pendingCountPersonal ?? payload?.pendingCount ?? 0,
        lastSavedAt: payload?.lastSavedAt ?? null,
      });
      setSnapshot(next);

      if (onLoadNationalId && data?.identification_id) {
        onLoadNationalId(String(data.identification_id));
      }
      setLoadedOnce(true);
    } catch (err) {
      console.error('fetchPersonalOverlay error', err);
      setError(err?.message || 'Failed to load personal info');
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load once when tab becomes active
  useEffect(() => {
    if (!active || loadedOnce) return;
    loadOverlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, loadedOnce]);

  useImperativeHandle(ref, () => ({
    // Parent sometimes needs national id
    getNationalId: () => (form?.identification_id || '').toString(),

    // Parent can force a fresh fetch (e.g., before opening review modal)
    refreshOverlay: async () => { await loadOverlay(true); },

    // Snapshot for "Review & Submit" popup
    getOverlaySnapshot: () => ({
      data: { ...form },
      flags: { ...flags },
      meta:  { ...meta },
    }),
  }));

  // -------- handlers (kept, even if edit UI is currently disabled) --------
  const handleEdit = () => {
    setSnapshot({ ...form });
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setForm({ ...snapshot });
    setIsEditing(false);
    setError(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setWorking(true);
    setError(null);
    try {
      const payload = {
        identification_id: form.identification_id || null,
        name_ar: form.name_ar || null,
        name_en: form.name_en || null,
        gender: form.gender || null, // 'male'/'female'
        dob_hijri: form.dob_hijri || null,
        dob_gregorian: form.dob_gregorian || form.date_of_birth || null,
      };

      const resp = await savePersonalDraft(payload);
      const data = resp?.data || emptyData;

      const next = {
        ...emptyData,
        ...data,
        date_of_birth: data?.date_of_birth || data?.dob_gregorian || '',
      };

      setForm(next);
      setFlags(resp?.flags || {});
      setMeta({
        pendingCount: resp?.pendingCountPersonal ?? resp?.pendingCount ?? 0,
        lastSavedAt: resp?.lastSavedAt ?? null,
      });
      setSnapshot(next);
      setIsEditing(false);

      if (onLoadNationalId) onLoadNationalId(String(data?.identification_id || ''));
    } catch (err) {
      console.error('savePersonalDraft error', err);
      setError(err?.message || 'Update failed');
    } finally {
      setWorking(false);
    }
  };

  const fmtDate = (d) => (d ? String(d).split('T')[0] : '—');

  // Don’t render before first activation; after it loads once, keep mounted/cached
  if (!active && !loadedOnce) return null;
  if (loading) return <CSpinner />;

  return (
    <div>
      {/* Banner when there are unsent changes */}
      {meta.pendingCount > 0 && (
        <CAlert color="warning" className="py-2 mb-3">
          {t('common:unsent_changes_with_count', { count: meta.pendingCount })}
        </CAlert>
      )}

      {/* Header + edit/save/cancel (edit is currently hidden) */}
      <div className="flex items-center justify-between mb-3">
        <h5 className="mb-0 text-xl font-bold text-gray-900">{t('tabs.personal', 'البيانات الشخصية')}</h5>
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {t('common:btn.edit', 'تعديل')}
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={working}
              className="inline-flex items-center gap-2 rounded-xl bg-sa-600 text-white px-4 py-2 text-sm font-semibold hover:bg-sa-700 transition-colors disabled:opacity-50"
            >
              {working ? <CSpinner size="sm" /> : t('common:btn.save', 'حفظ')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={working}
              className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {t('common:btn.cancel', 'إلغاء')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="text-danger mb-2">{error}</div>}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6" dir="rtl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            {
              key: 'identification_id',
              label: t('personal.identification_id', 'رقم الهوية الوطنية'),
              placeholder: t('personal.identification_id', 'رقم الهوية الوطنية'),
              dir: 'ltr',
            },
            {
              key: 'name_ar',
              label: t('personal.name_ar', 'الاسم الكامل (بالعربية)'),
              placeholder: t('personal.name_ar', 'الاسم الكامل (بالعربية)'),
              dir: 'rtl',
            },
            {
              key: 'name_en',
              label: t('personal.name_en', 'الاسم الكامل (بالإنجليزية)'),
              placeholder: t('personal.name_en', 'الاسم الكامل (بالإنجليزية)'),
              dir: 'ltr',
            },
          ].map((field) => (
            <div key={field.key} className="flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                {field.label}
                {!isEditing && form[field.key] && flags?.[field.key] === 'draft' && (
                  <CBadge color="info" className="text-xs">{t('common:draft', 'مسودة')}</CBadge>
                )}
              </span>
              <CFormInput
                value={form[field.key] || ''}
                onChange={(e) => handleChange(field.key, e.target.value)}
                placeholder={field.placeholder}
                readOnly={!isEditing}
                disabled={!isEditing}
                dir={field.dir}
                className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
              />
            </div>
          ))}

          <div className="flex flex-col text-right">
            <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
              {t('personal.gender', 'الجنس')}
              {!isEditing && form.gender && flags?.gender === 'draft' && (
                <CBadge color="info" className="text-xs">{t('common:draft', 'مسودة')}</CBadge>
              )}
            </span>
            {isEditing ? (
              <CFormSelect
                value={form.gender || ''}
                onChange={(e) => handleChange('gender', e.target.value)}
                className="rounded-xl border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sa-500"
              >
                <option value="">{t('personal.select', 'اختر')}</option>
                <option value="male">{t('personal.male', 'ذكر')}</option>
                <option value="female">{t('personal.female', 'أنثى')}</option>
              </CFormSelect>
            ) : (
              <CFormInput
                value={form.gender || ''}
                readOnly
                disabled
                placeholder={t('personal.select', 'اختر')}
                className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
              />
            )}
          </div>

          {[{
            key: 'dob_hijri',
            label: t('personal.dob_hijri', 'تاريخ الميلاد (هجري)'),
            placeholder: '1403-05-12',
          }, {
            key: 'dob_gregorian',
            label: t('personal.dob_gregorian', 'تاريخ الميلاد (ميلادي)'),
            placeholder: '1983-02-25',
          }].map(({ key, label, placeholder }) => (
            <div key={key} className="flex flex-col text-right">
              <span className="text-sm font-semibold text-gray-500 mb-1 flex items-center gap-2">
                {label}
                {!isEditing && form[key] && flags?.[key] === 'draft' && (
                  <CBadge color="info" className="text-xs">{t('common:draft', 'مسودة')}</CBadge>
                )}
              </span>
              <CFormInput
                value={form[key] || ''}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={placeholder}
                readOnly={!isEditing}
                disabled={!isEditing}
                dir="ltr"
                className={`rounded-xl border border-gray-300 px-4 py-3 text-right text-sm focus:outline-none focus:ring-2 focus:ring-sa-500 ${!isEditing ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
              />
            </div>
          ))}

          <div className="flex flex-col text-right">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('common:labels.updated_on', 'آخر تحديث')}</span>
            <CFormInput
              value={fmtDate(meta.lastSavedAt) || ''}
              readOnly
              disabled
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>

          <div className="flex flex-col text-right">
            <span className="text-sm font-semibold text-gray-500 mb-1">{t('profile:created_on', 'تاريخ الإنشاء')}</span>
            <CFormInput
              value={fmtDate(form.created_dt) || ''}
              readOnly
              disabled
              className="rounded-xl border border-gray-300 px-4 py-3 text-right text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default EmployeePersonalInfo;
